import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { FaLinkedin, FaInstagram, FaGithub, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
const staticTeam = [
  { name: "Manish Kumar", role: "Founder & CEO", skills: ["Business Strategy", "Leadership", "Vision"], color: "#0ea5e9", linkedin: "", instagram: "", github: "", image: "", emoji: "👨‍💼" },
  { name: "Rahul Sharma", role: "Lead Developer", skills: ["React.js", "Node.js", "MongoDB"], color: "#8b5cf6", linkedin: "", instagram: "", github: "", image: "", emoji: "👨‍💻" },
  { name: "Priya Singh", role: "UI/UX Designer", skills: ["Figma", "Adobe XD", "Branding"], color: "#ef4444", linkedin: "", instagram: "", github: "", image: "", emoji: "👩‍🎨" },
  { name: "Amit Verma", role: "Digital Marketing Head", skills: ["SEO", "Google Ads", "Meta Ads"], color: "#f59e0b", linkedin: "", instagram: "", github: "", image: "", emoji: "📊" },
  { name: "Neha Gupta", role: "Mobile App Developer", skills: ["Flutter", "Firebase", "Dart"], color: "#10b981", linkedin: "", instagram: "", github: "", image: "", emoji: "📱" },
];

function TeamCard({ member, i, inView }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientY - rect.top) / rect.height - 0.5) * 15;
    const y = -((e.clientX - rect.left) / rect.width - 0.5) * 15;
    setTilt({ x, y });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: i * 0.1 }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setTilt({ x: 0, y: 0 }); setHovered(false); }}
      style={{
        transform: hovered ? `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(1.03)` : "perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)",
        transition: "transform 0.15s ease",
        boxShadow: hovered ? `0 20px 60px ${member.color}30` : "none",
      }}
      className="glass rounded-2xl sm:rounded-3xl p-4 sm:p-8 group text-center cursor-default w-full"
    >
      <div className="absolute inset-0 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 0%, ${member.color}15, transparent 70%)` }} />

      <div className="relative inline-block mb-5">
        <motion.div
          animate={hovered ? { scale: 1.15, rotate: [0, -5, 5, 0] } : { scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-3xl sm:text-4xl mx-auto overflow-hidden"
          style={{ background: `${member.color}20` }}
        >
          {member.image
            ? <img src={`${process.env.REACT_APP_API_URL}${member.image}`} alt={member.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; }} />
            : member.emoji || "👤"
          }
        </motion.div>
        <motion.div
          animate={hovered ? { scale: 1.3 } : { scale: 1 }}
          className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-dark"
          style={{ background: member.color }}
        />
      </div>

      <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{member.name}</h3>
      <p className="text-xs sm:text-sm mb-4" style={{ color: member.color }}>{member.role}</p>

      <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-5 sm:mb-6">
        {member.skills.map((skill, si) => (
          <motion.span key={skill}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: i * 0.1 + si * 0.05 + 0.3 }}
            className="text-xs px-2.5 sm:px-3 py-1 rounded-full"
            style={{ background: `${member.color}15`, color: member.color }}>
            {skill}
          </motion.span>
        ))}
      </div>

      <div className="flex justify-center gap-3 sm:gap-4">
        {[
          { Icon: FaLinkedin, href: member.linkedin, label: "LinkedIn" },
          { Icon: FaInstagram, href: member.instagram, label: "Instagram" },
          { Icon: FaGithub, href: member.github, label: "GitHub" },
        ].filter(({ href }) => href).map(({ Icon, href, label }) => (
          <motion.a key={label} href={href} target="_blank" rel="noopener noreferrer"
            whileHover={{ scale: 1.25, y: -4 }} whileTap={{ scale: 0.9 }} aria-label={label}
            className="w-8 h-8 sm:w-9 sm:h-9 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors">
            <Icon className="text-xs sm:text-sm" />
          </motion.a>
        ))}
      </div>
    </motion.div>
  );
}

// Custom Arrow Components
function CustomPrevArrow({ onClick }) {
  return (
    <button
      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 z-10 w-10 h-10 sm:w-12 sm:h-12 glass rounded-full flex items-center justify-center text-blue-400 hover:text-white hover:bg-blue-500/20 transition-all duration-300 group"
      onClick={onClick}
      aria-label="Previous slide"
    >
      <FaChevronLeft className="text-sm sm:text-lg group-hover:scale-110 transition-transform" />
    </button>
  );
}

function CustomNextArrow({ onClick }) {
  return (
    <button
      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 z-10 w-10 h-10 sm:w-12 sm:h-12 glass rounded-full flex items-center justify-center text-blue-400 hover:text-white hover:bg-blue-500/20 transition-all duration-300 group"
      onClick={onClick}
      aria-label="Next slide"
    >
      <FaChevronRight className="text-sm sm:text-lg group-hover:scale-110 transition-transform" />
    </button>
  );
}

export default function Team() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [team, setTeam] = useState([]);

  useEffect(() => { setTeam(staticTeam); }, []);
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    swipe: true,
    touchMove: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          swipe: true,
          touchMove: true,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          swipe: true,
          touchMove: true,
          centerMode: false,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          swipe: true,
          touchMove: true,
          centerMode: false,
        }
      }
    ]
  };

  return (
    <section id="team" className="pt-8 sm:pt-12 lg:pt-16 pb-4 sm:pb-6 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-48 sm:w-64 lg:w-96 h-48 sm:h-64 lg:h-96 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-48 sm:w-64 lg:w-96 h-48 sm:h-64 lg:h-96 bg-blue-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }} className="text-center mb-8 sm:mb-12 lg:mb-16">
          <span className="text-blue-400 font-semibold text-xs sm:text-sm uppercase tracking-widest">Our People</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black mt-3 mb-4">
            Meet Our <span className="gradient-text">Team</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Our talented team of experts brings together diverse skills and experiences to deliver exceptional results.
          </p>
        </motion.div>

        <div className="relative px-0 sm:px-8">
          <Slider {...sliderSettings} className="team-slider">
            {team.map((member, i) => (
              <div key={member.name} className="px-2 sm:px-3 h-full">
                <TeamCard member={member} i={i} inView={inView} />
              </div>
            ))}
          </Slider>
        </div>
      </div>

      <style jsx="true" global="true">{`
        .team-slider .slick-slider {
          padding-bottom: 0 !important;
        }
        .team-slider .slick-list {
          padding-bottom: 0 !important;
        }
        .team-slider .slick-dots {
          position: relative !important;
          bottom: 0 !important;
          margin-top: 16px;
        }
        .team-slider .slick-dots li {
          margin: 0 3px;
        }
        .team-slider .slick-dots li button {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
          padding: 0;
        }
        .team-slider .slick-dots li button:before {
          display: none;
        }
        .team-slider .slick-dots li.slick-active button,
        .team-slider .slick-dots li button:hover {
          background: #0ea5e9;
          transform: scale(1.2);
        }
        .team-slider .slick-track {
          display: flex;
          align-items: stretch;
        }
        .team-slider .slick-slide {
          height: auto;
        }
        .team-slider .slick-slide > div {
          height: 100%;
        }
        @media (max-width: 767px) {
          .team-slider .slick-slide {
            width: 100vw !important;
          }
          .team-slider .slick-list {
            overflow: hidden !important;
          }
          .team-slider .slick-track {
            width: 100% !important;
          }
          .team-slider .slick-arrow {
            display: none !important;
          }
          .team-slider .slick-dots li button {
            width: 8px;
            height: 8px;
          }
        }
      `}</style>
    </section>
  );
}
